import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import { Webhook, Link2, AlertCircle } from 'lucide-react'

export default function WebhookSettings() {
  const [settings, setSettings] = useState({
    zapier_url: '',
    crm_url: '',
    crm_type: 'lofty' as 'lofty' | 'follow_up_boss' | 'custom',
    crm_api_key: '',
  })
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    fetchSettings()
  }, [])

  const fetchSettings = async () => {
    const { data } = await supabase
      .from('teams')
      .select('webhook_zapier_url, webhook_crm_url, webhook_crm_type, webhook_crm_api_key')
      .single()
    
    if (data) {
      setSettings({
        zapier_url: data.webhook_zapier_url || '',
        crm_url: data.webhook_crm_url || '',
        crm_type: data.webhook_crm_type || 'lofty',
        crm_api_key: data.webhook_crm_api_key || '',
      })
    }
    setLoading(false)
  }

  const handleSave = async () => {
    setSaving(true)
    
    const { error } = await supabase
      .from('teams')
      .update({
        webhook_zapier_url: settings.zapier_url,
        webhook_crm_url: settings.crm_url,
        webhook_crm_type: settings.crm_type,
        webhook_crm_api_key: settings.crm_api_key,
      })
      .eq('id', 'ef6b2a6d-0237-4fb3-ad09-15dba4bf7fb2')

    if (error) {
      alert('Error saving: ' + error.message)
    } else {
      alert('Settings saved!')
    }
    setSaving(false)
  }

  const testWebhook = async () => {
    const testPayload = {
      id: 'test-' + Date.now(),
      name: 'Test Lead',
      email: 'test@example.com',
      phone: '(480) 555-0100',
      source: 'test',
      team_id: 'test',
      team_name: 'Test Team',
      created_at: new Date().toISOString(),
    }

    try {
      if (settings.zapier_url) {
        await fetch(settings.zapier_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(testPayload),
        })
        alert('Zapier test sent!')
      }
      
      if (settings.crm_url) {
        await fetch(settings.crm_url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(settings.crm_api_key && { 'Authorization': `Bearer ${settings.crm_api_key}` }),
          },
          body: JSON.stringify(testPayload),
        })
        alert('CRM test sent!')
      }
    } catch (error) {
      alert('Test failed: ' + error)
    }
  }

  if (loading) return <div className="p-8">Loading...</div>

  return (
    <div className="p-8 max-w-2xl">
      <h1 className="text-2xl font-bold mb-6 flex items-center gap-2">
        <Webhook size={24} />
        Webhook Integrations
      </h1>

      <div className="bg-white rounded-lg shadow p-6 space-y-6">
        {/* Zapier */}
        <div>
          <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
            <Link2 size={18} />
            Zapier Integration
          </h2>
          <p className="text-sm text-gray-600 mb-3">
            Send all leads to Zapier for automation workflows.
          </p>
          <input
            type="url"
            placeholder="https://hooks.zapier.com/hooks/catch/..."
            value={settings.zapier_url}
            onChange={(e) => setSettings({ ...settings, zapier_url: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
          />
        </div>

        {/* CRM */}
        <div className="border-t pt-6">
          <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
            <Webhook size={18} />
            CRM Integration
          </h2>
          <p className="text-sm text-gray-600 mb-3">
            Direct integration with your CRM for instant lead creation.
          </p>
          
          <select
            value={settings.crm_type}
            onChange={(e) => setSettings({ ...settings, crm_type: e.target.value as any })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md mb-3"
          >
            <option value="lofty">Lofty</option>
            <option value="follow_up_boss">Follow Up Boss</option>
            <option value="custom">Custom</option>
          </select>
          
          <input
            type="url"
            placeholder="CRM Webhook URL"
            value={settings.crm_url}
            onChange={(e) => setSettings({ ...settings, crm_url: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md mb-3"
          />
          
          <input
            type="password"
            placeholder="CRM API Key (if required)"
            value={settings.crm_api_key}
            onChange={(e) => setSettings({ ...settings, crm_api_key: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
          />
        </div>

        {/* Info */}
        <div className="bg-blue-50 p-4 rounded-lg flex gap-3">
          <AlertCircle className="text-blue-600 flex-shrink-0" size={20} />
          <div className="text-sm text-blue-800">
            <p className="font-semibold">What gets sent?</p>
            <ul className="mt-1 space-y-1">
              <li>• QR scan leads (name, email, phone, listing code)</li>
              <li>• Landing page inquiries</li>
              <li>• Dashboard activity (optional)</li>
            </ul>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-4 pt-4">
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
          
          <button
            onClick={testWebhook}
            className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Send Test
          </button>
        </div>
      </div>
    </div>
  )
}
